import type { XInput } from '@playcastdotio/CoreTypes';
import { normalizePosition, normalizeLayout } from './utils';

export type Position = {
  x: string; // percentage from left
  y: string; // percentage from top
  scale: number; // scale factor
};

/**
 * Reference device dimensions for responsive scaling
 * iPhone 16 Pro Max in landscape mode
 */
export const REFERENCE_SCREEN = {
  width: 932, // landscape width
  height: 430, // landscape height
} as const;

export type GamepadLayout = {
  dpad: Position; // Legacy - kept for backwards compatibility
  // Individual d-pad buttons (used when useCircularButtons is true)
  dpadUp: Position;
  dpadDown: Position;
  dpadLeft: Position;
  dpadRight: Position;
  faceButtons: Position; // Legacy - kept for backwards compatibility
  // Individual face buttons (used when useCircularButtons is true)
  faceButtonA: Position;
  faceButtonB: Position;
  faceButtonX: Position;
  faceButtonY: Position;
  leftThumbstick: Position;
  rightThumbstick: Position;
  leftBumper: Position;
  rightBumper: Position;
  leftTrigger: Position;
  rightTrigger: Position;
  leftMeta: Position;
  rightMeta: Position;
  leftStick: Position;
  rightStick: Position;
};

export type TouchTracker = {
  touches: {
    [key: number]: {
      identifier: number;
      start: {
        x: number;
        y: number;
      };
    };
  };
};

export type MobileGamepadState = XInput & TouchTracker;

export type HiddenButtons = {
  [key: string]: boolean;
};

let mobileGamepad: MobileGamepadState = {
  wButtons: 0,
  bLeftTrigger: 0,
  bRightTrigger: 0,
  sThumbLX: 0,
  sThumbLY: 0,
  sThumbRX: 0,
  sThumbRY: 0,
  touches: {},
};

let isGamepadCustomizationOpen = false;
let isAdvanceSettingsGamepad = false;

const defaultLayout: GamepadLayout = {
  dpad: {
    x: '26%',
    y: '82%',
    scale: 0.75,
  },
  // Individual d-pad buttons (diamond layout around dpad center)
  dpadUp: {
    x: '26%',
    y: '76%',
    scale: 0.75,
  },
  dpadDown: {
    x: '26%',
    y: '88%',
    scale: 0.75,
  },
  dpadLeft: {
    x: '20%',
    y: '82%',
    scale: 0.75,
  },
  dpadRight: {
    x: '32%',
    y: '82%',
    scale: 0.75,
  },
  faceButtons: {
    x: '88%',
    y: '60%',
    scale: 1,
  },
  // Individual face buttons (diamond layout around faceButtons center)
  faceButtonY: {
    x: '88%',
    y: '52%',
    scale: 1,
  },
  faceButtonB: {
    x: '94%',
    y: '60%',
    scale: 1,
  },
  faceButtonA: {
    x: '88%',
    y: '68%',
    scale: 1,
  },
  faceButtonX: {
    x: '82%',
    y: '60%',
    scale: 1,
  },
  leftThumbstick: {
    x: '15%',
    y: '65%',
    scale: 1,
  },
  rightThumbstick: {
    x: '72%',
    y: '80%',
    scale: 0.75,
  },
  leftBumper: {
    x: '5%',
    y: '23%',
    scale: 1,
  },
  rightBumper: {
    x: '96%',
    y: '23%',
    scale: 1,
  },
  leftTrigger: {
    x: '6%',
    y: '10%',
    scale: 1,
  },
  rightTrigger: {
    x: '94%',
    y: '10%',
    scale: 1,
  },
  leftMeta: {
    x: '25%',
    y: '8%',
    scale: 1,
  },
  rightMeta: {
    x: '75%',
    y: '8%',
    scale: 1,
  },
  leftStick: {
    x: '5%',
    y: '75%',
    scale: 1,
  },
  rightStick: {
    x: '82%',
    y: '95%',
    scale: 1,
  },
};

const defaultHiddenButtons: HiddenButtons = {};

// Base storage keys - will be suffixed with context when set
const BASE_LAYOUT_KEY = 'playcast_gamepad_layout';
const BASE_OPACITY_KEY = 'playcast_gamepad_button_opacity';
const BASE_HIDDEN_BUTTONS_KEY = 'playcast_hidden_buttons';

const GLOBAL_DEFAULT_OPACITY = 80; // Global default opacity

// Current context (quickstart ID) - empty string means global/default settings
let currentContext = '';

// Theme-specific defaults (set per experience via config)
// These take precedence over global defaults but are overridden by user-saved settings
let themeDefaultLayout: Partial<GamepadLayout> = {};
let themeDefaultHiddenButtons: string[] = [];
let themeDefaultOpacity: number | null = null;
let themeUseCircularButtons = false;
let themeCustomButtonIcons: { [key: string]: string } = {};
let themeCustomButtonSizes: { [key: string]: number } = {};

/**
 * Theme defaults configuration interface
 */
export interface ThemeDefaultsConfig {
  defaultLayout?: Partial<GamepadLayout>;
  defaultHiddenButtons?: string[];
  defaultOpacity?: number;
  useCircularButtons?: boolean;
  customButtonIcons?: { [key: string]: string };
  customButtonSizes?: { [key: string]: number };
}

// Helper to get context-aware storage key
const getStorageKey = (baseKey: string): string => {
  return currentContext ? `${baseKey}_${currentContext}` : baseKey;
};

// Get effective default opacity (theme default takes precedence over global)
const getEffectiveDefaultOpacity = (): number => {
  return themeDefaultOpacity ?? GLOBAL_DEFAULT_OPACITY;
};

// Get effective default layout (merge theme defaults with global defaults)
const getEffectiveDefaultLayout = (): GamepadLayout => {
  // Normalize theme defaults to ensure % suffix (fixes theme data with missing %)
  const normalizedThemeDefaults = normalizeLayout(themeDefaultLayout);
  return { ...defaultLayout, ...normalizedThemeDefaults };
};

// Get effective default hidden buttons (theme defaults)
const getEffectiveDefaultHiddenButtons = (): HiddenButtons => {
  const result: HiddenButtons = { ...defaultHiddenButtons };
  for (const key of themeDefaultHiddenButtons) {
    // Handle legacy 'dpad' key - expand it to individual d-pad buttons
    if (key === 'dpad') {
      result['dpadUp'] = true;
      result['dpadDown'] = true;
      result['dpadLeft'] = true;
      result['dpadRight'] = true;
    }
    // Handle legacy 'faceButtons' key - expand it to individual face buttons
    else if (key === 'faceButtons') {
      result['faceButtonA'] = true;
      result['faceButtonB'] = true;
      result['faceButtonX'] = true;
      result['faceButtonY'] = true;
    }
    result[key] = true;
  }
  return result;
};

// Load opacity from localStorage or use default
const loadSavedOpacity = (): number => {
  try {
    const saved = localStorage.getItem(getStorageKey(BASE_OPACITY_KEY));
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn('Failed to load saved button opacity', e);
  }
  return getEffectiveDefaultOpacity();
};

let buttonOpacity = loadSavedOpacity();

// Load hidden buttons from localStorage or use default
const loadSavedHiddenButtons = (): HiddenButtons => {
  try {
    const saved = localStorage.getItem(getStorageKey(BASE_HIDDEN_BUTTONS_KEY));
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.warn('Failed to load saved hidden buttons', e);
  }
  return getEffectiveDefaultHiddenButtons();
};

let hiddenButtons: HiddenButtons = loadSavedHiddenButtons();

// Load layout from localStorage or use default
const loadSavedLayout = (): GamepadLayout => {
  try {
    const saved = localStorage.getItem(getStorageKey(BASE_LAYOUT_KEY));
    if (saved) {
      const parsedLayout = JSON.parse(saved);
      // Normalize positions to ensure % suffix (fixes legacy data)
      const normalized = normalizeLayout(parsedLayout);
      // Merge saved layout with defaults to ensure all keys exist
      // This handles cases where new layout keys are added (e.g., dpadUp, dpadDown)
      return { ...getEffectiveDefaultLayout(), ...normalized };
    }
  } catch (e) {
    console.warn('Failed to load saved gamepad layout', e);
  }
  return getEffectiveDefaultLayout();
};

let currentLayout: GamepadLayout = loadSavedLayout();

const subs = {
  gamepad: [] as ((gamepad: MobileGamepadState) => void)[],
  customization: [] as ((isOpen: boolean) => void)[],
  layout: [] as ((layout: GamepadLayout) => void)[],
  advanceSettings: [] as ((isOpen: boolean) => void)[],
  hiddenButtons: [] as ((hiddenButtons: HiddenButtons) => void)[],
  buttonOpacity: [] as ((opacity: number) => void)[],
  themeConfig: [] as ((config: { useCircularButtons: boolean; customButtonIcons: { [key: string]: string }; customButtonSizes: { [key: string]: number } }) => void)[],
};

const emitGamepadChange = () => {
  for (const s of subs.gamepad) {
    s(mobileGamepad);
  }
};

const emitCustomizationChange = () => {
  for (const s of subs.customization) {
    s(isGamepadCustomizationOpen);
  }
};

const emitLayoutChange = () => {
  for (const s of subs.layout) {
    s(currentLayout);
  }
};

const emitAdvanceSettingsChange = () => {
  for (const s of subs.advanceSettings) {
    s(isAdvanceSettingsGamepad);
  }
};

const emitHiddenButtonsChange = () => {
  for (const s of subs.hiddenButtons) {
    s(hiddenButtons);
  }
};

const emitButtonOpacityChange = () => {
  subs.buttonOpacity.forEach((callback) => callback(buttonOpacity));
};

const emitThemeConfigChange = () => {
  for (const s of subs.themeConfig) {
    s({ useCircularButtons: themeUseCircularButtons, customButtonIcons: themeCustomButtonIcons, customButtonSizes: themeCustomButtonSizes });
  }
};

export const gamepadStore = {
  updateGamepad(g: MobileGamepadState) {
    // If in customization mode, only update touch tracking but ignore button/input states
    if (isGamepadCustomizationOpen) {
      mobileGamepad = {
        ...mobileGamepad,
        touches: g.touches,
        // Reset all input states to 0/false when in customization mode
        wButtons: 0,
        bLeftTrigger: 0,
        bRightTrigger: 0,
        sThumbLX: 0,
        sThumbLY: 0,
        sThumbRX: 0,
        sThumbRY: 0,
      };
    } else {
      mobileGamepad = g;
    }
    emitGamepadChange();
  },
  subscribeGamepad(callback: (gamepad: MobileGamepadState) => void) {
    subs.gamepad = [...subs.gamepad, callback];
    return () => {
      subs.gamepad = subs.gamepad.filter((s) => s !== callback);
    };
  },
  getGamepad() {
    return mobileGamepad;
  },
  setIsGamepadCustomizationOpen(isOpen: boolean) {
    isGamepadCustomizationOpen = isOpen;
    // Close advance settings when exiting customization mode
    if (!isOpen) {
      isAdvanceSettingsGamepad = false;
      emitAdvanceSettingsChange();
    }
    emitCustomizationChange();
  },
  subscribeGamepadCustomization(callback: (isOpen: boolean) => void) {
    subs.customization = [...subs.customization, callback];
    return () => {
      subs.customization = subs.customization.filter((s) => s !== callback);
    };
  },
  getIsGamepadCustomizationOpen() {
    return isGamepadCustomizationOpen;
  },
  updateLayout(layout: Partial<GamepadLayout>) {
    currentLayout = {
      ...currentLayout,
      ...layout,
    };
    // Save to localStorage whenever layout is updated
    try {
      localStorage.setItem(getStorageKey(BASE_LAYOUT_KEY), JSON.stringify(currentLayout));
    } catch (e) {
      console.warn('Failed to save gamepad layout', e);
    }
    emitLayoutChange();
  },
  resetLayout() {
    currentLayout = getEffectiveDefaultLayout();
    try {
      localStorage.removeItem(getStorageKey(BASE_LAYOUT_KEY));
    } catch (e) {
      console.warn('Failed to remove saved gamepad layout', e);
    }
    emitLayoutChange();
  },
  subscribeLayout(callback: (layout: GamepadLayout) => void) {
    subs.layout = [...subs.layout, callback];
    return () => {
      subs.layout = subs.layout.filter((s) => s !== callback);
    };
  },
  getLayout() {
    return currentLayout;
  },
  /**
   * Calculate responsive scale factor based on current screen vs reference device
   * Uses the height dimension as the primary scaling factor for landscape mode
   * @param isForceRotated - If true, swap dimensions to match visual orientation
   */
  getResponsiveScale(isForceRotated?: boolean): number {
    let currentHeight = window.innerHeight;
    let currentWidth = window.innerWidth;

    // When force-rotated, swap dimensions to match visual orientation
    if (isForceRotated) {
      [currentWidth, currentHeight] = [currentHeight, currentWidth];
    }

    // Only apply responsive scaling in landscape mode
    if (currentWidth <= currentHeight) {
      return 1; // Portrait mode - use base scale
    }

    // Calculate scale based on height (the constraining dimension in landscape)
    // Use a minimum scale to prevent buttons from getting too small
    const heightScale = currentHeight / REFERENCE_SCREEN.height;

    // Clamp the scale between 0.7 and 1.3 to prevent extreme sizes
    return Math.max(0.7, Math.min(1.3, heightScale));
  },

  // Helper function to convert screen coordinates to percentages
  convertCoordsToPercentage(x: number, y: number): { x: string; y: string } {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    return {
      x: `${(x / viewportWidth) * 100}%`,
      y: `${(y / viewportHeight) * 100}%`,
    };
  },
  // Add new methods for advance settings
  setIsAdvanceSettingsGamepad(isOpen: boolean) {
    // Only allow opening advance settings in customization mode
    if (isOpen && !isGamepadCustomizationOpen) return;
    isAdvanceSettingsGamepad = isOpen;

    // Close customization mode when modal is open
    if (isOpen) {
      isGamepadCustomizationOpen = false;
      emitCustomizationChange();
    }

    emitAdvanceSettingsChange();
  },
  getIsAdvanceSettingsGamepad() {
    return isAdvanceSettingsGamepad;
  },
  subscribeAdvanceSettings(callback: (isOpen: boolean) => void) {
    subs.advanceSettings = [...subs.advanceSettings, callback];
    return () => {
      subs.advanceSettings = subs.advanceSettings.filter((s) => s !== callback);
    };
  },
  setButtonOpacity(opacity: number) {
    buttonOpacity = opacity;
    try {
      localStorage.setItem(getStorageKey(BASE_OPACITY_KEY), opacity.toString());
    } catch (e) {
      console.warn('Failed to save button opacity', e);
    }
    emitButtonOpacityChange();
  },
  getButtonOpacity() {
    return buttonOpacity;
  },
  subscribeButtonOpacity(callback: (opacity: number) => void) {
    subs.buttonOpacity = [...subs.buttonOpacity, callback];
    return () => {
      subs.buttonOpacity = subs.buttonOpacity.filter((s) => s !== callback);
    };
  },
  resetButtonOpacity() {
    buttonOpacity = getEffectiveDefaultOpacity();
    try {
      localStorage.removeItem(getStorageKey(BASE_OPACITY_KEY));
    } catch (e) {
      console.warn('Failed to remove saved button opacity', e);
    }
    emitButtonOpacityChange();
  },
  setButtonHidden(buttonKey: string, hidden: boolean) {
    hiddenButtons = {
      ...hiddenButtons,
      [buttonKey]: hidden,
    };
    try {
      localStorage.setItem(getStorageKey(BASE_HIDDEN_BUTTONS_KEY), JSON.stringify(hiddenButtons));
    } catch (e) {
      console.warn('Failed to save hidden buttons', e);
    }
    emitHiddenButtonsChange();
  },
  getHiddenButtons() {
    return hiddenButtons;
  },
  subscribeHiddenButtons(callback: (hiddenButtons: HiddenButtons) => void) {
    subs.hiddenButtons = [...subs.hiddenButtons, callback];
    return () => {
      subs.hiddenButtons = subs.hiddenButtons.filter((s) => s !== callback);
    };
  },
  resetHiddenButtons() {
    hiddenButtons = getEffectiveDefaultHiddenButtons();
    try {
      localStorage.removeItem(getStorageKey(BASE_HIDDEN_BUTTONS_KEY));
    } catch (e) {
      console.warn('Failed to remove saved hidden buttons', e);
    }
    emitHiddenButtonsChange();
  },
  /**
   * Set theme-specific defaults for the current experience.
   * These defaults are used when no user customization exists.
   * @param config - Theme defaults configuration
   * @returns true if defaults actually changed, false otherwise
   */
  setThemeDefaults(config: ThemeDefaultsConfig): boolean {
    // Normalize layout to ensure all positions have % suffix
    const newLayout = normalizeLayout(config.defaultLayout ?? {});
    const newHiddenButtons = config.defaultHiddenButtons ?? [];
    const newOpacity = config.defaultOpacity ?? null;
    const newUseCircular = config.useCircularButtons ?? false;
    const newCustomIcons = config.customButtonIcons ?? {};
    const newCustomSizes = config.customButtonSizes ?? {};

    // Check if anything actually changed
    const layoutChanged = JSON.stringify(themeDefaultLayout) !== JSON.stringify(newLayout);
    const hiddenChanged = JSON.stringify(themeDefaultHiddenButtons) !== JSON.stringify(newHiddenButtons);
    const opacityChanged = themeDefaultOpacity !== newOpacity;
    const circularChanged = themeUseCircularButtons !== newUseCircular;
    const iconsChanged = JSON.stringify(themeCustomButtonIcons) !== JSON.stringify(newCustomIcons);
    const sizesChanged = JSON.stringify(themeCustomButtonSizes) !== JSON.stringify(newCustomSizes);

    if (!layoutChanged && !hiddenChanged && !opacityChanged && !circularChanged && !iconsChanged && !sizesChanged) {
      return false; // No changes
    }

    themeDefaultLayout = newLayout as Partial<GamepadLayout>;
    themeDefaultHiddenButtons = newHiddenButtons;
    themeDefaultOpacity = newOpacity;
    themeUseCircularButtons = newUseCircular;
    themeCustomButtonIcons = newCustomIcons;
    themeCustomButtonSizes = newCustomSizes;

    console.log(`[GamepadStore] Theme defaults set:`, {
      hasLayout: Object.keys(themeDefaultLayout).length > 0,
      hiddenButtons: themeDefaultHiddenButtons,
      opacity: themeDefaultOpacity,
      useCircularButtons: themeUseCircularButtons,
      hasCustomIcons: Object.keys(themeCustomButtonIcons).length > 0,
      hasCustomSizes: Object.keys(themeCustomButtonSizes).length > 0,
    });

    // Emit theme config changes to subscribers
    emitThemeConfigChange();

    return true;
  },
  /**
   * Clear theme-specific defaults (reset to global defaults)
   * @returns true if any defaults were actually cleared, false if already empty
   */
  clearThemeDefaults(): boolean {
    const hadLayout = Object.keys(themeDefaultLayout).length > 0;
    const hadHiddenButtons = themeDefaultHiddenButtons.length > 0;
    const hadOpacity = themeDefaultOpacity !== null;
    const hadCircular = themeUseCircularButtons !== false;
    const hadIcons = Object.keys(themeCustomButtonIcons).length > 0;
    const hadSizes = Object.keys(themeCustomButtonSizes).length > 0;

    if (!hadLayout && !hadHiddenButtons && !hadOpacity && !hadCircular && !hadIcons && !hadSizes) {
      return false; // Nothing to clear
    }

    themeDefaultLayout = {};
    themeDefaultHiddenButtons = [];
    themeDefaultOpacity = null;
    themeUseCircularButtons = false;
    themeCustomButtonIcons = {};
    themeCustomButtonSizes = {};

    // Emit theme config changes to subscribers
    emitThemeConfigChange();

    return true;
  },
  /**
   * Set the context (e.g., quickstart ID) for per-game settings.
   * This will reload all settings from localStorage for the new context.
   * @param context - The context identifier (e.g., 'shades', 'arcraiders'). Empty string for global settings.
   * @param themeDefaults - Optional theme-specific defaults for this context
   */
  setContext(context: string, themeDefaults?: ThemeDefaultsConfig) {
    const contextChanged = currentContext !== context;
    let themeDefaultsChanged = false;

    // Update or clear theme defaults
    if (themeDefaults) {
      themeDefaultsChanged = gamepadStore.setThemeDefaults(themeDefaults);
    } else {
      // Clear theme defaults when none provided (regardless of context change)
      // This handles the case where a theme config is removed from the server
      themeDefaultsChanged = gamepadStore.clearThemeDefaults();
    }

    // Skip if neither context nor theme defaults changed
    if (!contextChanged && !themeDefaultsChanged) {
      return;
    }

    if (contextChanged) {
      currentContext = context;
      console.log(`[GamepadStore] Context changed to: ${context || 'global'}`);
    } else if (themeDefaultsChanged) {
      console.log(`[GamepadStore] Theme defaults updated for context: ${context || 'global'}`);
    }

    // Reload all settings for the new context (will use theme defaults as fallbacks)
    currentLayout = loadSavedLayout();
    buttonOpacity = loadSavedOpacity();
    hiddenButtons = loadSavedHiddenButtons();

    // Emit changes to all subscribers
    emitLayoutChange();
    emitButtonOpacityChange();
    emitHiddenButtonsChange();
  },
  /**
   * Get the current context (quickstart ID)
   */
  getContext() {
    return currentContext;
  },
  /**
   * Get the current theme defaults
   */
  getThemeDefaults(): ThemeDefaultsConfig {
    return {
      defaultLayout: themeDefaultLayout,
      defaultHiddenButtons: themeDefaultHiddenButtons,
      defaultOpacity: themeDefaultOpacity ?? undefined,
      useCircularButtons: themeUseCircularButtons,
      customButtonIcons: themeCustomButtonIcons,
      customButtonSizes: themeCustomButtonSizes,
    };
  },
  /**
   * Get whether circular buttons mode is enabled for current theme
   */
  getUseCircularButtons(): boolean {
    return themeUseCircularButtons;
  },
  /**
   * Get custom button icons for current theme
   */
  getCustomButtonIcons(): { [key: string]: string } {
    return themeCustomButtonIcons;
  },
  /**
   * Get custom button sizes for current theme
   */
  getCustomButtonSizes(): { [key: string]: number } {
    return themeCustomButtonSizes;
  },
  /**
   * Subscribe to theme config changes (useCircularButtons, customButtonIcons, customButtonSizes)
   */
  subscribeThemeConfig(callback: (config: { useCircularButtons: boolean; customButtonIcons: { [key: string]: string }; customButtonSizes: { [key: string]: number } }) => void) {
    subs.themeConfig = [...subs.themeConfig, callback];
    return () => {
      subs.themeConfig = subs.themeConfig.filter((s) => s !== callback);
    };
  },
};
